import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';
import { relevamientoService, Relevamiento, getEstadoColor } from '../services/relevamientoService';
import { tareaService, EstadoTarea, User } from '../services/tareaService';
import { getTiposElemento, TipoElemento } from '../services/tipoElementoService';
import DataTable from '../components/DataTable';
import ViewToggle from '../components/ViewToggle';
import Notification from '../components/Notification';
import { sucursalService } from '../services/sucursalService';
import { useListUrlState } from '../hooks/useListUrlState';
import { hasActiveListFilters } from '../utils/listSearch';

const RelevamientosPage: React.FC = () => {
  const [relevamientos, setRelevamientos] = useState<Relevamiento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [estadosMap, setEstadosMap] = useState<Record<number, string>>({});
  const [usuariosMap, setUsuariosMap] = useState<Record<number, string>>({});
  const [tiposElementoMap, setTiposElementoMap] = useState<Record<number, string>>({});
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [filterOptions, setFilterOptions] = useState<{
    clientes: { value: number; label: string }[];
    estados: { value: number; label: string }[];
    tiposProducto: { value: string; label: string }[];
  } | null>(null);
  const [sucursalOptions, setSucursalOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});
  const [catalogsReady, setCatalogsReady] = useState(false);
  const navigate = useNavigate();
  const { filtersFromUrl, writeUrlState, searchParams } = useListUrlState(
    'estadoTarea,tipoProducto,fecha_desde,fecha_hasta',
  );
  const selectedClienteRef = useRef(selectedCliente);
  selectedClienteRef.current = selectedCliente;

  const loadRelevamientos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await relevamientoService.getAll();
      setRelevamientos(data);
    } catch (error) {
      console.error('Error loading relevamientos:', error);
      setNotification({
        type: 'error',
        message: 'Error al cargar los relevamientos',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = useCallback(
    async (filters: Record<string, any>) => {
      setCurrentFilters(filters);

      const clienteId = filters.cliente ? String(filters.cliente) : '';
      if (clienteId !== selectedClienteRef.current) {
        setSelectedCliente(clienteId);
        if (clienteId) {
          try {
            const sucursales = await sucursalService.getByClienteId(Number(clienteId));
            setSucursalOptions(sucursales.map((s) => ({ value: s.id, label: s.nombre })));
          } catch {
            setSucursalOptions([]);
          }
        } else {
          setSucursalOptions([]);
        }
      }

      if (hasActiveListFilters(filters)) {
        try {
          setLoading(true);
          const params: any = { pageSize: 100 };
          if (filters.q) params.q = filters.q;
          if (filters.cliente) params.clienteId = Number(filters.cliente);
          if (filters.sucursal) params.sucursalId = Number(filters.sucursal);
          if (filters.estadoTarea) params.estadoTareaId = filters.estadoTarea;
          if (filters.tipoProducto) params.tipoProducto = filters.tipoProducto;
          if (filters.fecha_desde) params.fechaDesde = filters.fecha_desde;
          if (filters.fecha_hasta) params.fechaHasta = filters.fecha_hasta;

          const searchResult = await relevamientoService.search(params);
          setRelevamientos(searchResult.data);
        } catch (error) {
          console.error('Error al buscar relevamientos:', error);
          setNotification({
            type: 'error',
            message: 'Error al buscar relevamientos',
          });
        } finally {
          setLoading(false);
        }
      } else {
        await loadRelevamientos();
      }
    },
    [loadRelevamientos],
  );

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        setLoading(true);
        const [estados, usuarios, tiposElemento, options] = await Promise.all([
          tareaService.getEstadosDeTarea(),
          tareaService.getUsers(),
          getTiposElemento(),
          relevamientoService.getFilterOptions(),
        ]);

        const eMap = (estados as EstadoTarea[]).reduce<Record<number, string>>((acc, e) => {
          acc[e.id] = e.nombre;
          return acc;
        }, {});
        const uMap = (usuarios as User[]).reduce<Record<number, string>>((acc, u) => {
          acc[u.id] = u.alias;
          return acc;
        }, {});
        const teMap = (tiposElemento as TipoElemento[]).reduce<Record<number, string>>((acc, t) => {
          acc[t.id] = t.nombre;
          return acc;
        }, {});

        setEstadosMap(eMap);
        setUsuariosMap(uMap);
        setTiposElementoMap(teMap);
        setFilterOptions(options);
        setCatalogsReady(true);
      } catch (err) {
        console.error('Error cargando catálogos de relevamientos:', err);
        setNotification({
          type: 'error',
          message: 'Error al cargar las opciones de filtros',
        });
        setCatalogsReady(true);
      }
    };

    void loadCatalogs();
  }, []);

  useEffect(() => {
    if (!catalogsReady) return;
    void applyFilters({ ...filtersFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogsReady, searchParams]);

  const handleView = (relevamiento: Relevamiento) => {
    navigate(`/relevamientos/${relevamiento.id}`);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    writeUrlState(filters, 1);
  };

  const columns = [
    {
      key: 'estadoTarea',
      label: 'Estado',
      render: (value: any, row: Relevamiento) => {
        const nombre = row?.estadoTarea?.nombre || estadosMap[row?.estadoTareaId as number];
        const id = row?.estadoTarea?.id ?? row?.estadoTareaId;
        const color = id != null && id > 0 ? getEstadoColor(id) : getEstadoColor(nombre ?? '');
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${color}`}>
            {nombre || 'N/A'}
          </span>
        );
      },
    },
    {
      key: 'fecha',
      label: 'Fecha',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">
          {row?.fecha ? new Date(row.fecha).toLocaleDateString() : 'N/A'}
        </div>
      ),
    },
    {
      key: 'fechaFin',
      label: 'Fecha Fin',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">
          {row?.fechaFin ? new Date(row.fechaFin).toLocaleDateString() : 'N/A'}
        </div>
      ),
    },
    {
      key: 'usuario',
      label: 'Usuario',
      render: (value: any, row: Relevamiento) => {
        const nombreCompleto =
          (row as any)?.usuario?.apellido && (row as any)?.usuario?.nombre
            ? `${(row as any).usuario.apellido}, ${(row as any).usuario.nombre}`
            : undefined;
        return (
          <div className="text-gray-900 dark:text-gray-100">
            {nombreCompleto || usuariosMap[row?.usuarioId as number] || 'N/A'}
          </div>
        );
      },
    },
    {
      key: 'cliente',
      label: 'Cliente',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">
          {row?.sucursal?.cliente?.nombre || 'N/A'}
        </div>
      ),
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">{row?.sucursal?.nombre || 'N/A'}</div>
      ),
    },
    {
      key: 'tipoRelevamiento',
      label: 'TIPO',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">
          {row?.tipoDeProducto?.nombre || 'N/A'}
        </div>
      ),
    },
    {
      key: 'tipoDeElemento',
      label: 'Tipo inst. fija',
      render: (value: any, row: Relevamiento) => {
        const esInstFijas = (row?.tipoDeProducto?.nombre || '').toLowerCase().includes('instal');
        const nombre =
          row?.tipoDeElemento?.nombre || tiposElementoMap[row?.tipoDeElementoId as number];
        return (
          <div className="text-gray-900 dark:text-gray-100">
            {esInstFijas ? nombre || 'N/A' : '—'}
          </div>
        );
      },
    },
    {
      key: 'fechaRecepcion',
      label: 'Fecha de recepción del servidor',
      render: (value: any, row: Relevamiento) => (
        <div className="text-gray-900 dark:text-gray-100">
          {row?.fechaRecepcion ? new Date(row.fechaRecepcion).toLocaleDateString() : 'N/A'}
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: 'Ver',
      icon: EyeIcon,
      onClick: handleView,
      className: 'text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300',
    },
  ];

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {relevamientos.map((relevamiento) => (
        <div key={relevamiento.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Relevamiento #{relevamiento.id}
              </h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  <span className="font-medium">Fecha:</span>{' '}
                  {new Date(relevamiento.fecha).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">Fecha Fin:</span>{' '}
                  {relevamiento.fechaFin
                    ? new Date(relevamiento.fechaFin).toLocaleDateString()
                    : 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Tarea:</span> {relevamiento.tarea?.nombre || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Sucursal:</span>{' '}
                  {relevamiento.sucursal?.nombre || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Cliente:</span>{' '}
                  {relevamiento.sucursal?.cliente?.nombre || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Producto:</span>{' '}
                  {relevamiento.tipoDeProducto?.nombre || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Usuario:</span>{' '}
                  {(() => {
                    const u = (relevamiento as any)?.usuario;
                    const nombreCompleto =
                      u?.apellido && u?.nombre ? `${u.apellido}, ${u.nombre}` : undefined;
                    return nombreCompleto || usuariosMap[relevamiento.usuarioId as number] || 'N/A';
                  })()}
                </div>
              </div>
            </div>
            <div className="flex flex-col space-y-2 ml-4">
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(() => {
                  const id = relevamiento.estadoTarea?.id ?? relevamiento.estadoTareaId;
                  return id != null && id > 0
                    ? getEstadoColor(id)
                    : getEstadoColor(relevamiento.estadoTarea?.nombre ?? '');
                })()}`}
              >
                {relevamiento.estadoTarea?.nombre || 'N/A'}
              </span>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handleView(relevamiento)}
              className="p-2 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              title="Ver"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="text-gray-600 dark:text-gray-400">Cargando relevamientos...</p>
        {error && <p className="text-red-600 dark:text-red-400 text-sm">Error: {error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Relevamientos</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Últimos 100. Usá filtros o al menos 3 letras para buscar en todos.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {viewMode === 'table' ? (
        <DataTable
          data={relevamientos}
          columns={columns}
          actions={actions}
          searchPlaceholder="Buscar por ID, tarea, sucursal, cliente o producto..."
          filters={[
            {
              key: 'cliente',
              label: 'Cliente',
              type: 'select',
              options: filterOptions?.clientes || [],
            },
            {
              key: 'sucursal',
              label: 'Sucursal',
              type: 'select',
              dependsOn: 'cliente',
              options: sucursalOptions,
            },
            {
              key: 'estadoTarea',
              label: 'Estado',
              type: 'select',
              options:
                filterOptions?.estados ||
                Object.entries(estadosMap).map(([id, nombre]) => ({
                  value: parseInt(id),
                  label: nombre,
                })),
            },
            {
              key: 'tipoProducto',
              label: 'Tipo Producto',
              type: 'select',
              options: filterOptions?.tiposProducto || [],
            },
            {
              key: 'fecha',
              label: 'Fecha',
              type: 'daterange',
            },
          ]}
          enableAdvancedFilters={true}
          onFilterChange={handleFilterChange}
          initialFilters={currentFilters}
        />
      ) : (
        renderGridView()
      )}
    </div>
  );
};

export default RelevamientosPage;
